import type { FormField } from '@lucid/lego';
import type { RegistrationFormDefaultValues } from './Registration';
import { getRegistrationStatusOptions, getDeliveryTimeOptions } from './constants';

const LICENSE_PLATE_ISSUANCE_CODE_PATTERN = /^[A-Za-z0-9]{0,6}$/;
const REGISTRATION_CODE_1_PATTERN = /^[A-Za-z0-9]{0,4}$/;
const REGISTRATION_CODE_2_PATTERN = /^[A-Za-z0-9]{0,5}$/;
const REGISTRATION_ORDER_OTT_PATTERN = /^[A-Za-z0-9]{0,10}$/;

/**
 * Creates form fields configuration for Registration Details form
 *
 * @param labels - i18n labels for form field labels and placeholders
 * @param defaultValues - Default values for registration fields
 * @param vehicleReadyToScheduleDate - Vehicle ready to schedule date for validation
 * @param isNL - Whether the user's country is NL, gates NL-specific field behavior
 * @returns Array of FormField configurations
 */
export const createRegistrationFormFields = (
  labels?: Record<string, string>,
  defaultValues?: RegistrationFormDefaultValues,
  vehicleReadyToScheduleDate?: string,
  isNL?: boolean
): FormField[] => {
  // Calculate minimum date for scheduledDeliveryDate
  // Must be more than both vehicleReadyToScheduleDate and today
  const getMinScheduledDate = (): string => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    let minDate = today;

    if (vehicleReadyToScheduleDate) {
      const readyDate = new Date(vehicleReadyToScheduleDate);
      readyDate.setHours(0, 0, 0, 0);

      // Use the later of vehicleReadyToScheduleDate or today
      if (readyDate > today) {
        minDate = readyDate;
      }
    }

    // Add 1 day to ensure it's "more than" not "equal to or greater than"
    minDate.setDate(minDate.getDate() + 1);

    return minDate.toISOString().split('T')[0];
  };

  const minScheduledDeliveryDate = vehicleReadyToScheduleDate ? getMinScheduledDate() : undefined;

  return [
    {
      id: 'firstRegistrationGroup',
      fieldType: 'group',
      title: labels?.['firstRegistration.details'] ?? 'First Registration Details',
      width: 'full',
      variant: 'flat',
      children: [
        {
          id: 'firstRegistrationDate',
          fieldType: 'date_picker',
          name: 'firstRegistrationDate',
          apiKey: 'firstRegistrationDate',
          label: labels?.['firstRegistration.date'] ?? 'First Registration Date',
          placeholder: labels?.['date.placeholder'] ?? 'Select a date',
          required: false,
          width: 'third',
          defaultValue: defaultValues?.firstRegistrationDate,
        },
        {
          id: 'licensePlateNumber',
          fieldType: 'text',
          name: 'licensePlateNumber',
          apiKey: 'licensePlateNumber',
          label: labels?.['licensePlate.number'] ?? 'License Plate Number',
          placeholder: labels?.['licensePlate.placeholder'] ?? 'Enter license plate number',
          disabled: isNL,
          required: false,
          width: 'third',
          defaultValue: defaultValues?.licensePlateNumber,
        },
        {
          id: 'registrationStatus',
          fieldType: 'dropdown',
          name: 'registrationStatus',
          apiKey: 'registrationStatus',
          label: labels?.['registration.status'] ?? 'Registration Status',
          placeholder: labels?.['status.placeholder'] ?? 'Select a status',
          required: false,
          width: 'third',
          options: getRegistrationStatusOptions(labels),
          defaultValue: defaultValues?.registrationStatus,
        },
      ],
    },
    ...(isNL
      ? [
          {
            id: 'licensePlateIssuanceCode',
            fieldType: 'text' as const,
            name: 'licensePlateIssuanceCode',
            apiKey: 'licensePlateIssuanceCode',
            label:
              labels?.['nlRegistration.licensePlateIssuanceCode.label'] ??
              'License Plate Issuance Code',
            placeholder:
              labels?.['nlRegistration.licensePlateIssuanceCode.placeholder'] ??
              'Enter License Plate Issuance Code',
            required: false,
            width: 'third' as const,
            defaultValue: defaultValues?.licensePlateIssuanceCode,
            pattern: LICENSE_PLATE_ISSUANCE_CODE_PATTERN,
            validationMessages: {
              pattern:
                labels?.['nlRegistration.licensePlateIssuanceCode.invalid'] ??
                'Must be up to 6 alphanumeric characters',
            },
            disabled: true,
          },
          {
            id: 'registrationCode1',
            fieldType: 'text' as const,
            name: 'registrationCode1',
            apiKey: 'registrationCode1',
            label: labels?.['nlRegistration.registrationCode1.label'] ?? 'Registration Code 1',
            placeholder:
              labels?.['nlRegistration.registrationCode1.placeholder'] ??
              'Enter Registration Code 1',
            required: false,
            width: 'third' as const,
            defaultValue: defaultValues?.registrationCode1,
            pattern: REGISTRATION_CODE_1_PATTERN,
            validationMessages: {
              pattern:
                labels?.['nlRegistration.registrationCode1.invalid'] ??
                'Must be up to 4 alphanumeric characters',
            },
            disabled: true,
          },
          {
            id: 'registrationCode2',
            fieldType: 'text' as const,
            name: 'registrationCode2',
            apiKey: 'registrationCode2',
            label: labels?.['nlRegistration.registrationCode2.label'] ?? 'Registration Code 2',
            placeholder:
              labels?.['nlRegistration.registrationCode2.placeholder'] ??
              'Enter Registration Code 2',
            required: false,
            width: 'third' as const,
            defaultValue: defaultValues?.registrationCode2,
            pattern: REGISTRATION_CODE_2_PATTERN,
            validationMessages: {
              pattern:
                labels?.['nlRegistration.registrationCode2.invalid'] ??
                'Must be up to 5 alphanumeric characters',
            },
            disabled: true,
          },
          {
            id: 'registrationOrderOtt',
            fieldType: 'text' as const,
            name: 'registrationOrderOtt',
            apiKey: 'registrationOrderOtt',
            label:
              labels?.['nlRegistration.registrationOrderOtt.label'] ?? 'Registration Order (OTT)',
            placeholder:
              labels?.['nlRegistration.registrationOrderOtt.placeholder'] ??
              'Enter Registration Order (OTT)',
            required: false,
            width: 'third' as const,
            defaultValue: defaultValues?.registrationOrderOtt,
            pattern: REGISTRATION_ORDER_OTT_PATTERN,
            validationMessages: {
              pattern:
                labels?.['nlRegistration.registrationOrderOtt.invalid'] ??
                'Must be up to 10 alphanumeric characters',
            },
            disabled: true,
          },
        ]
      : []),
    {
      id: 'scheduledDeliveryGroup',
      fieldType: 'group',
      title: labels?.['scheduledDelivery.dates'] ?? 'Scheduled Delivery Dates',
      width: 'full',
      variant: 'flat',
      children: [
        {
          id: 'scheduledDeliveryDate',
          fieldType: 'date_picker',
          name: 'scheduledDeliveryDate',
          apiKey: 'scheduledDeliveryDate',
          label: labels?.['scheduledDelivery.date'] ?? 'Scheduled Delivery Date',
          placeholder: labels?.['date.placeholder'] ?? 'Select a date',
          // Note: Both required and disabled when vehicleReadyToScheduleDate is missing.
          // This is intentional - it blocks form submission until the prerequisite
          // (Vehicle Ready to Schedule Date) is met, providing clear user feedback.
          required: !vehicleReadyToScheduleDate,
          width: 'half',
          defaultValue: defaultValues?.scheduledDeliveryDate,
          minDate: minScheduledDeliveryDate,
          disabled: !vehicleReadyToScheduleDate,
          validationMessages: {
            required:
              labels?.['vehicleReadyToSchedule.required'] ??
              'Vehicle Ready to Schedule Date needs to be populated',
          },
        },
        {
          id: 'scheduledDeliveryTime',
          fieldType: 'dropdown',
          name: 'scheduledDeliveryTime',
          apiKey: 'scheduledDeliveryTime',
          label: labels?.['scheduledDelivery.time'] ?? 'Scheduled Delivery Time',
          placeholder: labels?.['time.placeholder'] ?? 'Select a time',
          required: false,
          width: 'half',
          options: getDeliveryTimeOptions(labels),
          defaultValue: defaultValues?.scheduledDeliveryTime,
          disabled: !vehicleReadyToScheduleDate,
        },
      ],
    },
    {
      id: 'formButtons',
      fieldType: 'buttonGroup',
      buttons: [
        {
          id: 'submitButton',
          label: labels?.['form.save'] ?? 'Save',
          type: 'primary',
          size: 'medium',
          color: 'neutral',
          theme: 'light',
          isFluid: true,
        },
      ],
    },
  ];
};
